import React from "react";
import PropTypes from "prop-types";
import { slaBadgeClass } from "../slaUtils";

const PRIORITY_ICON_COLOR = {
  critical: "var(--t2t-danger)",
  high:     "var(--t2t-danger)",
  medium:   "var(--t2t-warning)",
  low:      "var(--t2t-text-muted)",
};

const STATUS_BADGE = {
  in_progress: "t2t-badge t2t-badge-in_progress",
  approved:    "t2t-badge t2t-badge-approved",
  rejected:    "t2t-badge t2t-badge-rejected",
  on_hold:     "t2t-badge t2t-badge-on_hold",
  draft:       "t2t-badge t2t-badge-draft",
  pending:     "t2t-badge t2t-badge-pending",
};

const PRIORITY_BADGE = {
  critical: "t2t-badge t2t-badge-critical",
  high:     "t2t-badge t2t-badge-high",
  medium:   "t2t-badge t2t-badge-medium",
  low:      "t2t-badge t2t-badge-low",
};

export default function RequestDetails({ request }) {
  if (!request) return null;
  const {
    stage_name, assigned_role_name, assignee_name, priority,
    created_at, due_at, sla, workflow_name, request_type_name,
  } = request;
  const status = request.request_status || request.status;

  return (
    <div className="row g-3 py-2">
      <Detail icon="bi-diagram-3"   label="Workflow"       value={workflow_name} />
      <Detail icon="bi-layers"      label="Stage"          value={stage_name} />
      <Detail icon="bi-people"      label="Assigned Role"  value={assigned_role_name} />
      <Detail icon="bi-person"      label="Assignee"       value={assignee_name} />
      <Detail icon="bi-tag"         label="Request Type"   value={request_type_name} />
      <Detail
        icon="bi-flag"
        iconColor={PRIORITY_ICON_COLOR[priority] || "var(--t2t-text-muted)"}
        label="Priority"
        value={priority
          ? <span className={PRIORITY_BADGE[priority] || "t2t-badge t2t-badge-low"} style={{ textTransform: "capitalize" }}>{priority}</span>
          : "—"}
      />
      <Detail
        icon="bi-circle-fill"
        iconColor="var(--t2t-text-muted)"
        label="Status"
        value={<span className={STATUS_BADGE[status] || "t2t-badge t2t-badge-draft"} style={{ textTransform: "capitalize" }}>{status?.replace(/_/g, " ")}</span>}
      />
      {sla && (
        <Detail
          icon="bi-clock"
          label="SLA"
          value={<span className={slaBadgeClass(sla.status)}>{sla.label || "No SLA"}</span>}
        />
      )}
      {due_at && (
        <Detail
          icon="bi-calendar-check"
          label="Due"
          value={new Date(due_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        />
      )}
      <Detail
        icon="bi-calendar3"
        label="Submitted"
        value={new Date(created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}
      />
    </div>
  );
}

RequestDetails.propTypes = {
  request: PropTypes.shape({
    stage_name:         PropTypes.string,
    assigned_role_name: PropTypes.string,
    assignee_name:      PropTypes.string,
    priority:           PropTypes.string,
    // BUG-02: backend stores status as integer enum; older REST shapes return string.
    // Accept both — runtime code already maps both forms via _statusLabel.
    status:             PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    request_status:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at:         PropTypes.string,
    due_at:             PropTypes.string,
    sla:                PropTypes.object,
    workflow_name:      PropTypes.string,
    request_type_name:  PropTypes.string,
  }),
};

function Detail({ icon, iconColor, label, value }) {
  return (
    <div className="col-6">
      <div className="d-flex align-items-start gap-2">
        <i
          className={`bi ${icon} mt-1`}
          style={{ fontSize: 13, minWidth: 14, color: iconColor || "var(--t2t-text-muted)" }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--t2t-text-muted)", marginBottom: 2 }}>
            {label}
          </div>
          <div style={{ fontSize: 13, color: "var(--t2t-text-primary)", wordBreak: "break-word" }}>{value || "—"}</div>
        </div>
      </div>
    </div>
  );
}

Detail.propTypes = {
  icon:      PropTypes.string.isRequired,
  iconColor: PropTypes.string,
  label:     PropTypes.string.isRequired,
  value:     PropTypes.node,
};
